import React, { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form as AntdForm } from 'antd';
import { useSelector } from 'react-redux';
import { routes } from '../../../../../utils/helpers/ApiClient';
import AppBarCMS from '../../../../basic/Navigation/AppBarCMS/AppBarCMS';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import { AdminContentBlock, Spinner } from '../../../../common';
import Form from '../../../../antd/form/Form/Form';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { applyPhdValidationErrors } from '../utils/phdApiFeedback.js';
import {
  useDeletePhdStudentEvaluationGradeMutation,
  useGetPhdStudentEvaluationGradeQuery,
  useLazyGetPhdStudentEvaluationDocumentQuery,
  useArchivePhdStudentEvaluationDocumentMutation,
  useSavePhdStudentEvaluationGradeMutation,
} from '../../../../../redux/services/phd';
import { usePhdReferenceData } from '../usePhdReferenceData.jsx';
import PhdStudentEvaluationsEditForm from './PhdStudentEvaluationsEditForm.jsx';
import { downloadBlobResponse } from '../../../../../utils/helpers/downloadBlobResponse';
import useIsUserPhdAdmin from '../../../../../hooks/useIsUserPhdAdmin.jsx';
import { usePhdDashboardPrefill } from '../utils/usePhdDashboardPrefill.js';
import { usePhdDirtyLeaveGuard } from '../usePhdDirtyLeaveGuard.jsx';
import PhdArchivedRecordNotice from '../PhdArchivedRecordNotice.jsx';

const dateFields = [
  'date_planned_dissertation_application',
  'date_planned_dissertation_submission',
  'date_planned_dissertation_exam',
];

const toDayjs = (value) => (value ? dayjs(value) : null);

const PhdStudentEvaluationGradesEdit = ({ mode }) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = AntdForm.useForm();
  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const user = useSelector((state) => state.auth.user);
  const isAdmin = useIsUserPhdAdmin();
  const dashboardPrefill = usePhdDashboardPrefill();
  const pageTitle = isCreate
    ? t('Pridanie zadania známok doktorandovi')
    : t('Editácia zadania známok doktorandovi');

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  const { data: detail, isLoading } = useGetPhdStudentEvaluationGradeQuery(id, {
    skip: !isEdit,
  });
  const [saveEvaluation, { isLoading: isSaving }] =
    useSavePhdStudentEvaluationGradeMutation();
  const [deleteEvaluation] = useDeletePhdStudentEvaluationGradeMutation();
  const [downloadEvaluationDoc, { isLoading: isDownloading }] =
    useLazyGetPhdStudentEvaluationDocumentQuery();
  const [archiveEvaluationDoc, { isLoading: isArchiving }] =
    useArchivePhdStudentEvaluationDocumentMutation();
  const { saveSuccess, saveFailed, deleteSuccess, deleteFailed } =
    useToastMessages();

  const {
    studentDisplayNameMap,
    students,
    schoolYears,
    schoolYearOptions,
    ensureSchoolYearSelection,
    isCreatingSchoolYear,
    isLoading: isReferenceLoading,
  } = usePhdReferenceData();

  const initials = useMemo(() => {
    if (isCreate) {
      return {
        phd_student_id: dashboardPrefill.phd_student_id,
        subjects_grades: undefined,
      };
    }

    if (!detail) {
      return null;
    }

    const mapped = { ...detail };
    dateFields.forEach((field) => {
      mapped[field] = toDayjs(detail[field]);
    });
    return mapped;
  }, [dashboardPrefill, detail, isCreate]);
  const { modal, onSaveFailed, onSaveSuccess, onValuesChange } =
    usePhdDirtyLeaveGuard(form, initials);
  const isReadOnly = detail?.is_read_only === true;

  const handleSubmit = async (values) => {
    const payload = { ...values };
    dateFields.forEach((field) => {
      payload[field] = values[field]
        ? values[field].format('YYYY-MM-DD')
        : null;
    });
    try {
      await saveEvaluation({
        id: isEdit ? detail.id : undefined,
        body: payload,
      }).unwrap();
      saveSuccess();
      onSaveSuccess(() => navigate(routes.cmsPhdStudentEvaluationGrades));
    } catch (error) {
      onSaveFailed();
      applyPhdValidationErrors(form, error);
      saveFailed(error);
    }
  };

  const handleDownload = async () => {
    if (!detail?.linked_evaluation_id) return;
    try {
      const response = detail.linked_evaluation_is_frozen
        ? await downloadEvaluationDoc(detail.linked_evaluation_id).unwrap()
        : await archiveEvaluationDoc(detail.linked_evaluation_id).unwrap();
      downloadBlobResponse(
        response,
        `${detail.student_display_name || t('Hodnotenie študenta')}.docx`,
      );
    } catch (error) {
      saveFailed(error);
    }
  };

  const handleDelete = async () => {
    if (!detail?.id) return;

    try {
      await deleteEvaluation(detail.id).unwrap();
      deleteSuccess();
      navigate(routes.cmsPhdStudentEvaluationGrades);
    } catch (error) {
      deleteFailed(error);
    }
  };

  const mainActions = useMemo(() => {
    if (!isEdit || !detail) return [];
    return [
      {
        onClick: handleDownload,
        title: detail.linked_evaluation_is_frozen
          ? t('Stiahnuť DOCX')
          : isAdmin
            ? t('Vydať a stiahnuť archívny DOCX')
            : t('Archívnu verziu môže vydať iba administrátor'),
        icon: <i className="fa fa-download" />,
        permission: 'phd-student-evaluation-grades.index',
        disabled:
          isDownloading ||
          isArchiving ||
          !detail.linked_evaluation_id ||
          (!detail.linked_evaluation_is_frozen && !isAdmin),
      },
    ];
  }, [detail, isAdmin, isArchiving, isDownloading, isEdit, t]);

  if ((isEdit && isLoading) || isReferenceLoading || !initials) {
    return <Spinner />;
  }

  return (
    <>
      {!isReadOnly && modal}
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsPhdStudentEvaluationGrades}
        backUrlTooltip={t('Zadanie známok doktorandom')}
        permissionsBasic="phd-student-evaluation-grades"
        mainActions={mainActions}
        secondaryActions={
          isEdit && !isReadOnly
            ? [
                {
                  onClick: handleDelete,
                  title: t('Vymazať'),
                  icon: <i className="fa fa-trash" />,
                  permission: 'phd-student-evaluation-grades.destroy',
                  confirmation: t('Naozaj chcete vymazať túto položku?'),
                },
              ]
            : []
        }
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako funguje zadávanie známok?')}
          description={t(
            'Vyberte doktoranda a hodnotený školský rok, doplňte výsledky predmetov za tento rok a záznam uložte. Odovzdané známky nemožno meniť bežným postupom študenta.',
          )}
        />
        <AdminContentBlock
          icon="fa fa-file-text"
          link={routes.cmsPhdStudentEvaluationGrades}
          title={pageTitle}
        >
          <PhdArchivedRecordNotice record={detail} />
          <Form
            form={form}
            initialValues={initials}
            onFinish={handleSubmit}
            onValuesChange={onValuesChange}
            disabled={isReadOnly}
          >
            <PhdStudentEvaluationsEditForm
              studentDisplayNameMap={studentDisplayNameMap}
              students={students}
              schoolYears={schoolYears}
              currentUserPersonId={user?.person_id}
              isAdmin={isAdmin}
              isEdit={isEdit}
              currentEvaluationId={detail?.id}
              schoolYearOptions={schoolYearOptions}
              ensureSchoolYearSelection={ensureSchoolYearSelection}
              isCreatingSchoolYear={isCreatingSchoolYear}
              isSubmitting={isSaving}
              serviceMode="grades"
              isReadOnly={isReadOnly}
            />
          </Form>
        </AdminContentBlock>
      </div>
    </>
  );
};

export default PhdStudentEvaluationGradesEdit;
